import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
  IconButton,
  Stack,
} from '@mui/material';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import Cropper, { Area } from 'react-easy-crop';
import { getCroppedImageFile, isValidPixelCrop } from '../utils/cropImage';
import { fileToDataUrl } from '../utils/fileToDataUrl';

export type CropAspectKey = '3:2' | '4:3' | '1:1' | 'free';

const ASPECT_VALUES: Record<CropAspectKey, number | undefined> = {
  '3:2': 3 / 2,
  '4:3': 4 / 3,
  '1:1': 1,
  free: undefined,
};

interface PhotoCropDialogProps {
  open: boolean;
  file: File | null;
  onCancel: () => void;
  /** Called with the cropped JPEG file when the user confirms framing. */
  onConfirm: (croppedFile: File) => void;
  /** Upload/save the original file without cropping. */
  onSkip?: (originalFile: File) => void;
  title?: string;
  confirmLabel?: string;
  skipLabel?: string;
  /** When false, hide the skip-framing action (e.g. reframe existing photos). */
  showSkip?: boolean;
}

const PhotoCropDialog: React.FC<PhotoCropDialogProps> = ({
  open,
  file,
  onCancel,
  onConfirm,
  onSkip,
  title = 'Frame your photo',
  confirmLabel = 'Use this framing',
  skipLabel = 'Upload without framing',
  showSkip = true,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspectKey, setAspectKey] = useState<CropAspectKey>('3:2');
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const aspect = ASPECT_VALUES[aspectKey];
  const cropReady = isValidPixelCrop(croppedAreaPixels);

  useEffect(() => {
    let cancelled = false;

    if (!open || !file) {
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
      setError(null);
      setWorking(false);
      setLoadFailed(false);
      return;
    }

    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    setError(null);
    setWorking(false);
    setLoadFailed(false);
    setImageSrc(null);

    // data: URLs (not blob:) — HSL CSP blocks blob: images in the cropper.
    fileToDataUrl(file)
      .then((dataUrl) => {
        if (!cancelled) setImageSrc(dataUrl);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setLoadFailed(true);
          setError(
            err?.message ||
              'Could not load this photo for framing. Use “Upload without framing” instead.'
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, file]);

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setCroppedAreaPixels(isValidPixelCrop(pixels) ? pixels : null);
  }, []);

  const helperText = useMemo(
    () =>
      'Drag to move the frame, zoom in/out, and rotate. Choose an aspect ratio for shop cards (3:2) or free crop.',
    []
  );

  const handleConfirm = async () => {
    if (!imageSrc || !file || !isValidPixelCrop(croppedAreaPixels)) {
      setError(
        'Invalid crop selection — move the frame onto the photo, or skip framing.'
      );
      return;
    }
    setWorking(true);
    setError(null);
    try {
      const cropped = await getCroppedImageFile(
        imageSrc,
        croppedAreaPixels!,
        file.name || 'set-photo.jpg',
        0.9,
        rotation
      );
      onConfirm(cropped);
    } catch (err: any) {
      setError(
        `${err?.message || 'Could not crop this photo'} You can still skip framing.`
      );
    } finally {
      setWorking(false);
    }
  };

  const handleSkip = () => {
    if (!file || working) return;
    if (onSkip) {
      onSkip(file);
      return;
    }
    onConfirm(file);
  };

  return (
    <Dialog
      open={open}
      onClose={working ? undefined : onCancel}
      fullWidth
      maxWidth="md"
      aria-labelledby="photo-crop-title"
    >
      <DialogTitle id="photo-crop-title">{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {helperText}
        </Typography>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={aspectKey}
          onChange={(_e, value: CropAspectKey | null) => {
            if (value) setAspectKey(value);
          }}
          sx={{ mb: 1.5, flexWrap: 'wrap' }}
          aria-label="Crop aspect ratio"
          disabled={working || loadFailed}
        >
          <ToggleButton value="3:2">3:2 shop</ToggleButton>
          <ToggleButton value="4:3">4:3</ToggleButton>
          <ToggleButton value="1:1">Square</ToggleButton>
          <ToggleButton value="free">Free</ToggleButton>
        </ToggleButtonGroup>

        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: { xs: 280, sm: 360 },
            bgcolor: '#111',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {imageSrc ? (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              showGrid
              objectFit="contain"
            />
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'grey.400',
                px: 2,
                textAlign: 'center',
              }}
            >
              {loadFailed
                ? 'Photo preview unavailable — use skip framing.'
                : 'Loading photo…'}
            </Box>
          )}
        </Box>

        <Stack spacing={1.5} sx={{ mt: 2, px: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Zoom
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.05}
              aria-label="Zoom"
              onChange={(_e, value) => setZoom(value as number)}
              disabled={!imageSrc || working || loadFailed}
            />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Rotate
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                aria-label="Rotate left"
                onClick={() => setRotation((r) => (r - 90 + 360) % 360)}
                disabled={!imageSrc || working || loadFailed}
                size="small"
              >
                <RotateLeftIcon />
              </IconButton>
              <Slider
                value={rotation}
                min={0}
                max={360}
                step={1}
                aria-label="Rotation"
                onChange={(_e, value) => setRotation(value as number)}
                disabled={!imageSrc || working || loadFailed}
                sx={{ flex: 1 }}
              />
              <IconButton
                aria-label="Rotate right"
                onClick={() => setRotation((r) => (r + 90) % 360)}
                disabled={!imageSrc || working || loadFailed}
                size="small"
              >
                <RotateRightIcon />
              </IconButton>
              <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'right' }}>
                {Math.round(rotation)}°
              </Typography>
            </Box>
          </Box>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onCancel} disabled={working}>
          Cancel
        </Button>
        {showSkip && (
          <Button onClick={handleSkip} disabled={!file || working} variant="outlined">
            {skipLabel}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!imageSrc || !cropReady || working || loadFailed}
          startIcon={working ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {working ? 'Saving…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhotoCropDialog;
